import { DocumentSection, EntityDocument } from '@toggle/toggle';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Markdown } from '~/components/markdown/Markdown';
import { DocumentProvider } from '~/modules/document-provider/DocumentProvider';
import { useDocuments } from '~/react-query-hooks/documents/useDocumentsEntity';

import styles from './Citations.module.scss';

export interface CitationsProps {
  citations: DocumentSection[];
  onClick: (citationId: string, document: EntityDocument) => void;
  activeIdx?: number;
  ref?: React.RefObject<(HTMLDivElement | null)[]>;
}

export const Citations = ({
  citations,
  onClick,
  activeIdx,
  ref,
}: CitationsProps) => {
  const { t } = useTranslation(['widget']);

  const { data } = useDocuments(citations.map(c => c.document));

  useEffect(() => {
    if (activeIdx !== undefined) {
      const activeRef = ref?.current?.[activeIdx];
      activeRef?.scrollIntoView({
        block: 'nearest',
      });
    }
  }, []);

  return (
    <>
      <p className={styles.header}>{t('widget:citations.passage')}</p>
      {citations.map((citation, idx) => {
        const document = data.find(d => d?.id === citation.document);

        if (!document) {
          return null;
        }

        const { kind, period, year } = document;
        const isActive = idx === activeIdx;

        return (
          <div
            key={idx}
            className={isActive ? styles.containerActive : styles.container}
            ref={(el: HTMLDivElement | null) => {
              if (ref?.current) {
                ref.current[idx] = el;
              }
            }}
            onClick={() => onClick(citation.id, document)}
            data-testid={`citation-${idx}`}
          >
            <span className={styles.markdownWrapper}>
              <Markdown message={`"${citation.content}"`} />
            </span>
            <DocumentProvider
              documentKind={kind}
              date={`${year} ${period.toUpperCase()}`}
            />
          </div>
        );
      })}
    </>
  );
};
